import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { FilterBar } from '../../components/FilterBar';
import { advertisementService, Advertisement, AdvertisementStats } from '../../services/advertisementService';

export const AdminAdvertisements: React.FC = () => {
  const [allAds, setAllAds] = useState<Advertisement[]>([]);
  const [ads, setAds] = useState<Advertisement[]>([]);
  const [stats, setStats] = useState<AdvertisementStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<Advertisement | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    advertiser_name: '',
    advertiser_email: '',
    advertiser_phone: '',
    image: null as File | null,
    link_url: '',
    description: '',
    position: 'sidebar',
    start_date: '',
    end_date: '',
    status: 'inactive',
    price: '0',
    payment_status: 'pending',
  });
  const [filters, setFilters] = useState({
    position: '',
    status: '',
  });

  useEffect(() => {
    fetchAds();
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allAds, currentPage]);

  const fetchAds = async () => {
    setLoading(true);
    try {
      const data = await advertisementService.getAll(1);
      console.log('Annonces récupérées:', data);
      
      // Vérifier si c'est une réponse paginée ou un tableau simple
      let adsList: Advertisement[] = [];
      
      if (Array.isArray(data)) {
        adsList = data;
      } else if (data && 'results' in data) {
        adsList = data.results;
      }
      
      setAllAds(adsList);
    } catch (error) {
      console.error('Erreur lors de la récupération:', error);
      setAllAds([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allAds];

    // Filtre par position
    if (filters.position) {
      filtered = filtered.filter((ad) => ad.position === filters.position);
    }

    // Filtre par statut
    if (filters.status) {
      filtered = filtered.filter((ad) => ad.status === filters.status);
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || a.start_date || 0).getTime();
      const dateB = new Date((b as any).created_at || b.start_date || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const pageSize = 10;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedAds = filtered.slice(startIndex, endIndex);

    setAds(paginatedAds);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const fetchStats = async () => {
    try {
      const data = await advertisementService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = new FormData();
      
      // Ajouter tous les champs requis
      payload.append('title', formData.title);
      payload.append('advertiser_name', formData.advertiser_name);
      payload.append('advertiser_email', formData.advertiser_email);
      if (formData.advertiser_phone) {
        payload.append('advertiser_phone', formData.advertiser_phone);
      }
      if (formData.image) {
        payload.append('image', formData.image);
      }
      payload.append('link_url', formData.link_url);
      if (formData.description) {
        payload.append('description', formData.description);
      }
      payload.append('position', formData.position);
      // Le format datetime-local est déjà compatible avec Django
      payload.append('start_date', formData.start_date || new Date().toISOString());
      if (formData.end_date) {
        payload.append('end_date', formData.end_date);
      }
      payload.append('status', formData.status);
      payload.append('price', formData.price);
      payload.append('payment_status', formData.payment_status);

      await advertisementService.create(payload as any);
      setIsCreateModalOpen(false);
      resetForm();
      // Réinitialiser à la page 1 après création
      setCurrentPage(1);
      await fetchAds();
      await fetchStats();
      applyFilters();
    } catch (error: any) {
      console.error('Erreur complète:', error);
      const errorData = error.response?.data;
      let errorMessage = 'Une erreur est survenue';
      
      if (errorData) {
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'object') {
          // Afficher les erreurs de validation
          const errors = Object.entries(errorData)
            .map(([key, value]: [string, any]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(', ')}`;
              }
              return `${key}: ${value}`;
            })
            .join('\n');
          errorMessage = errors || JSON.stringify(errorData);
        }
      }
      
      alert(`Erreur lors de la création:\n\n${errorMessage}\n\nVérifiez que tous les champs requis sont remplis.`);
    }
  };

  const handleEdit = (ad: Advertisement) => {
    setSelectedAd(ad);
    
    // Formater les dates pour le champ datetime-local
    const formatDateForInput = (dateString: string) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch (e) {
        console.error('Erreur de formatage de date:', dateString, e);
        return '';
      }
    };
    
    setFormData({
      title: ad.title,
      advertiser_name: ad.advertiser_name,
      advertiser_email: ad.advertiser_email,
      advertiser_phone: ad.advertiser_phone || '',
      image: null,
      link_url: ad.link_url,
      description: ad.description || '',
      position: ad.position,
      start_date: formatDateForInput(ad.start_date),
      end_date: ad.end_date ? formatDateForInput(ad.end_date) : '',
      status: ad.status,
      price: ad.price.toString(),
      payment_status: ad.payment_status,
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAd) return;

    try {
      const payload = new FormData();
      
      // Ajouter tous les champs
      payload.append('title', formData.title);
      payload.append('advertiser_name', formData.advertiser_name);
      payload.append('advertiser_email', formData.advertiser_email);
      if (formData.advertiser_phone) {
        payload.append('advertiser_phone', formData.advertiser_phone);
      }
      if (formData.image) {
        payload.append('image', formData.image);
      }
      payload.append('link_url', formData.link_url);
      if (formData.description) {
        payload.append('description', formData.description);
      }
      payload.append('position', formData.position);
      payload.append('start_date', formData.start_date);
      if (formData.end_date) {
        payload.append('end_date', formData.end_date);
      }
      payload.append('status', formData.status);
      payload.append('price', formData.price);
      payload.append('payment_status', formData.payment_status);

      await advertisementService.update(selectedAd.id, payload as any);
      setIsEditModalOpen(false);
      resetForm();
      setSelectedAd(null);
      await fetchAds();
      await fetchStats();
      applyFilters();
    } catch (error: any) {
      console.error('Erreur:', error);
      const errorMessage = error.response?.data || 'Une erreur est survenue';
      alert(`Erreur: ${JSON.stringify(errorMessage)}`);
    }
  };

  const handleDelete = async () => {
    if (!selectedAd) return;

    try {
      await advertisementService.delete(selectedAd.id);
      setIsDeleteModalOpen(false);
      setSelectedAd(null);
      await fetchAds();
      await fetchStats();
      applyFilters();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Une erreur est survenue');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      advertiser_name: '',
      advertiser_email: '',
      advertiser_phone: '',
      image: null,
      link_url: '',
      description: '',
      position: 'sidebar',
      start_date: '',
      end_date: '',
      status: 'inactive',
      price: '0',
      payment_status: 'pending',
    });
  };

  const positions = [
    { value: 'header', label: 'En-tête (Header)' },
    { value: 'sidebar', label: 'Barre latérale (Sidebar)' },
    { value: 'content_top', label: 'Au-dessus du contenu' },
    { value: 'content_bottom', label: 'En bas du contenu' },
    { value: 'between_content', label: 'Entre les contenus' },
    { value: 'footer', label: 'Pied de page (Footer)' },
  ];

  return (
    <AdminLayout activeSection="advertisements">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-green-700">Gestion des Annonces Publicitaires</h2>
        <p className="text-gray-600 mt-1">Créez et gérez les annonces publicitaires sur votre site</p>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="mt-4 btn-primary"
        >
          + Nouvelle Annonce
        </button>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <p className="text-gray-600 text-sm mb-1">Total Annonces</p>
            <p className="text-3xl font-bold text-green-700">{stats.total_ads}</p>
            <p className="text-xs text-gray-500 mt-1">{stats.active_ads} actives</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <p className="text-gray-600 text-sm mb-1">Total Impressions</p>
            <p className="text-3xl font-bold text-blue-700">{stats.total_impressions.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
            <p className="text-gray-600 text-sm mb-1">Total Clics</p>
            <p className="text-3xl font-bold text-purple-700">{stats.total_clicks.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-amber-500">
            <p className="text-gray-600 text-sm mb-1">Revenus Totaux</p>
            <p className="text-3xl font-bold text-amber-700">{stats.total_revenue.toLocaleString()} FCFA</p>
          </div>
        </div>
      )}

      {/* Filtres */}
      <FilterBar
        searchPlaceholder=""
        searchValue=""
        onSearchChange={() => {}}
        filters={[
          {
            key: 'position',
            label: 'Toutes les positions',
            value: filters.position,
            onChange: (value) => {
              setFilters({ ...filters, position: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'sidebar', label: '📋 Sidebar' },
              { value: 'header', label: '📊 Header' },
              { value: 'footer', label: '🔻 Footer' },
              { value: 'banner', label: '📢 Banner' },
            ],
          },
          {
            key: 'status',
            label: 'Tous les statuts',
            value: filters.status,
            onChange: (value) => {
              setFilters({ ...filters, status: value });
              setCurrentPage(1);
            },
            options: [
              { value: 'active', label: '✅ Actives' },
              { value: 'inactive', label: '⏸️ Inactives' },
            ],
          },
        ]}
        onClear={() => {
          setFilters({ position: '', status: '' });
          setCurrentPage(1);
        }}
      />

      {/* Liste des annonces */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Liste des Annonces</h3>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-700"></div>
          </div>
        ) : ads.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Image</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Titre</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Annonceur</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Position</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Statut</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Impressions</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Clics</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Prix</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ads.map((ad) => (
                    <tr key={ad.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <img src={ad.image} alt={ad.title} className="w-16 h-16 object-cover rounded-lg" />
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-medium text-gray-900">{ad.title}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-gray-700">{ad.advertiser_name}</div>
                        <div className="text-xs text-gray-500">{ad.advertiser_email}</div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                          {ad.position_display}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {ad.status === 'active' ? (
                          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                            ✅ {ad.status_display}
                          </span>
                        ) : (
                          <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-xs font-semibold">
                            ⏸️ {ad.status_display}
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">
                        {ad.impressions.toLocaleString()}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">
                        {ad.clicks.toLocaleString()} ({ad.click_through_rate}%)
                      </td>
                      <td className="py-4 px-4">
                        <div className="font-semibold text-green-700">{ad.price.toLocaleString()} FCFA</div>
                        <div className="text-xs text-gray-500">{ad.payment_status_display}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(ad)}
                            className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                          >
                            ✏️ Modifier
                          </button>
                          <button
                            onClick={() => {
                              setSelectedAd(ad);
                              setIsDeleteModalOpen(true);
                            }}
                            className="text-red-600 hover:text-red-800 font-semibold text-sm"
                          >
                            🗑️ Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading}
              onPageChange={(page) => {
                setCurrentPage(page);
              }}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📢</div>
            <p className="text-gray-600 text-lg">Aucune annonce pour le moment</p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="mt-4 btn-primary"
            >
              Créer la première annonce
            </button>
          </div>
        )}
      </div>

      {/* Modal Création */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Nouvelle Annonce"
        size="lg"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsCreateModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={handleCreate} className="btn-primary">
              Créer
            </button>
          </div>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre de l'annonce *</label>
            <input
              type="text"
              className="input-field"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'annonceur *</label>
              <input
                type="text"
                className="input-field"
                value={formData.advertiser_name}
                onChange={(e) => setFormData({ ...formData, advertiser_name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                className="input-field"
                value={formData.advertiser_email}
                onChange={(e) => setFormData({ ...formData, advertiser_email: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
            <input
              type="text"
              className="input-field"
              value={formData.advertiser_phone}
              onChange={(e) => setFormData({ ...formData, advertiser_phone: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Image de l'annonce *</label>
            <input
              type="file"
              accept="image/*"
              className="input-field"
              onChange={(e) => setFormData({ ...formData, image: e.target.files?.[0] || null })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">URL de destination *</label>
            <input
              type="url"
              className="input-field"
              value={formData.link_url}
              onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Position *</label>
            <select
              className="input-field"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              required
            >
              {positions.map((pos) => (
                <option key={pos.value} value={pos.value}>
                  {pos.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de début *</label>
              <input
                type="datetime-local"
                className="input-field"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de fin</label>
              <input
                type="datetime-local"
                className="input-field"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut *</label>
              <select
                className="input-field"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="scheduled">Programmée</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prix (FCFA) *</label>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut du paiement</label>
            <select
              className="input-field"
              value={formData.payment_status}
              onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
            >
              <option value="pending">En attente</option>
              <option value="paid">Payé</option>
              <option value="unpaid">Non payé</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              className="input-field min-h-[100px]"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
        </form>
      </Modal>

      {/* Modal Modification */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Modifier l'Annonce"
        size="lg"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsEditModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={handleUpdate} className="btn-primary">
              Enregistrer
            </button>
          </div>
        }
      >
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Titre de l'annonce *</label>
            <input
              type="text"
              className="input-field"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'annonceur *</label>
              <input
                type="text"
                className="input-field"
                value={formData.advertiser_name}
                onChange={(e) => setFormData({ ...formData, advertiser_name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
              <input
                type="email"
                className="input-field"
                value={formData.advertiser_email}
                onChange={(e) => setFormData({ ...formData, advertiser_email: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
            <input
              type="text"
              className="input-field"
              value={formData.advertiser_phone}
              onChange={(e) => setFormData({ ...formData, advertiser_phone: e.target.value })}
            />
          </div>

          {selectedAd && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Image actuelle</label>
              <img src={selectedAd.image} alt={selectedAd.title} className="w-32 h-32 object-cover rounded-lg mb-2" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nouvelle image (optionnel)</label>
            <input
              type="file"
              accept="image/*"
              className="input-field"
              onChange={(e) => setFormData({ ...formData, image: e.target.files?.[0] || null })}
            />
            <p className="text-xs text-gray-500 mt-1">Laisser vide pour conserver l'image actuelle</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">URL de destination *</label>
            <input
              type="url"
              className="input-field"
              value={formData.link_url}
              onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Position *</label>
            <select
              className="input-field"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              required
            >
              {positions.map((pos) => (
                <option key={pos.value} value={pos.value}>
                  {pos.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de début *</label>
              <input
                type="datetime-local"
                className="input-field"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date de fin</label>
              <input
                type="datetime-local"
                className="input-field"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Statut *</label>
              <select
                className="input-field"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="scheduled">Programmée</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prix (FCFA) *</label>
              <input
                type="number"
                step="0.01"
                className="input-field"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Statut du paiement</label>
            <select
              className="input-field"
              value={formData.payment_status}
              onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
            >
              <option value="pending">En attente</option>
              <option value="paid">Payé</option>
              <option value="unpaid">Non payé</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              className="input-field min-h-[100px]"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
        </form>
      </Modal>

      {/* Modal Suppression */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Supprimer l'Annonce"
        footer={
          <div className="flex gap-4">
            <button onClick={() => setIsDeleteModalOpen(false)} className="btn-secondary">
              Annuler
            </button>
            <button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg">
              Supprimer
            </button>
          </div>
        }
      >
        <p>Êtes-vous sûr de vouloir supprimer cette annonce ?</p>
        {selectedAd && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="font-semibold">{selectedAd.title}</p>
            <p className="text-sm text-gray-600">{selectedAd.advertiser_name}</p>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

